import { Pressable } from '@atlaskit/primitives/compiled';
import { css } from '@compiled/react';
import { componentWithFG } from '@confluence/feature-gate-component';
import { fg } from '@confluence/feature-gating';
import memoizeOne from 'memoize-one';
import React, { PureComponent, type CSSProperties } from 'react';
import type { WrappedComponentProps } from 'react-intl';
import { injectIntl } from 'react-intl';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';
import window from 'window-or-global';
import { token } from '@atlaskit/tokens';

import { DataSourceProvider as ReferentialityDataSourceProvider } from '@atlassian/editor-referentiality';

import type { DocNode } from '@atlaskit/adf-schema';
import { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { EmojiResource } from '@atlaskit/emoji/resource';
import { ReactRenderer as AkRenderer } from '@atlaskit/renderer';
import type { ObjectKey, TaskState } from '@atlaskit/task-decision';
import type ProfileClient from '@atlaskit/profilecard/client';
import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';
import { cssMap } from '@atlaskit/css';

import { isPageSSRd } from '@confluence/content-smartlinks/entry-points/isPageSSRd.ts';
import { ContentRefetchHandler } from '@confluence/content-body/entry-points/ContentRefetchHandler.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { getSmartCardRenderers } from '@confluence/fabric-extension-lib/entry-points/getSmartCardRenderers.ts';
import type { RendererExtensionHandlers } from '@confluence/fabric-extension-lib/entry-points/extensionTypes.ts';
import { ConfluenceSmartCardProvider } from '@confluence/smart-card-provider/entry-points/ConfluenceSmartCardProvider.tsx';
import { ConfluenceEditorCardProvider } from '@confluence/smartlinks';

import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { createSmartCardEventHandler } from '@confluence/adf-renderer/entry-points/createSmartCardEventHandler.ts';
import {
	registerGlobalMediaViewedListener,
	removeGlobalMediaViewedListener,
} from '@confluence/adf-renderer/entry-points/AttachmentEvents.ts';
import { ANALYTICS_EVENT_SEVERITY_TRACKING } from '@confluence/adf-renderer/entry-points/ADFRendererComponent.ts';
import { FloatingBannerStateContainer } from '@confluence/banners/entry-points/FloatingBannerStateContainer.ts';
import { BannerStateContainer } from '@confluence/banners/entry-points/BannerStateContainer.ts';
import type { TaskAction } from '@confluence/fabric-media-support/entry-points/TaskDecisionProvider.ts';
import type { ProfileCardGetActions } from '@confluence/fabric-media-support/entry-points/ProfileCardProvider.ts';
import { getMediaFeatureFlags } from '@confluence/fabric-media-support/entry-points/mediaFeatureFlags.ts';
import {
	SetBatchedTaskStatusMutation,
	TaskBatcher,
	ConfluenceTaskDecisionProvider,
} from '@confluence/fabric-media-support/entry-points/TaskDecisionProvider.ts';
import { getProfileCardProvider } from '@confluence/fabric-media-support/entry-points/ProfileCardProvider.ts';
import { createMentionsConfig } from '@confluence/fabric-media-support/entry-points/MentionsProvider.ts';
import { createRendererTemplateMediaProvider } from '@confluence/fabric-media-support/entry-points/TemplateMediaProvider.ts';
import { createRendererMediaProvider } from '@confluence/fabric-media-support/entry-points/MediaProvider.ts';
import { getEmojiProvider } from '@confluence/fabric-providers/entry-points/EmojiProvider.ts';
import { getCanViewUserProfile } from '@confluence/global-operations/entry-points/getCanViewUserProfile.ts';
import { WithAnnotationsWrapper } from '@confluence/content-renderer/entry-points/WithAnnotationsWrapper.ts';
import { messages } from '@confluence/content-renderer/entry-points/styled.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { getAtlassianPeopleProfileUrl } from '@confluence/profile/entry-points/helpers.ts';
import type { RoutesContextType } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import type { WithNavigationPolicyProps } from '@confluence/route-manager/entry-points/useNavigationPolicy.ts';
import { withRoutesContext } from '@confluence/route-manager/entry-points/withRoutesContext.ts';
import { withNavigationPolicy } from '@confluence/route-manager/entry-points/useNavigationPolicy.ts';

import type { CoreInvitesContextType } from '@confluence/core-invites-provider/entry-points/CoreInvitesContext.ts';
import { withCoreInvitesContext } from '@confluence/core-invites-provider/entry-points/withCoreInvitesContext.ts';
import type { ExternalShareContextType } from '@confluence/external-share-context/entry-points/ExternalShareContext.ts';
import { withExternalShareContext } from '@confluence/external-share-context/entry-points/withExternalShareContext.ts';
import type {
	FeatureFlagsType,
	SessionDataType,
} from '@confluence/session-data/entry-points/SessionDataTypes.ts';
import type { SessionDataContextInjected } from '@confluence/session-data/entry-points/withSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { withSessionData } from '@confluence/session-data/entry-points/withSessionData.ts';

import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { RendererAnalyticsListener } from '@confluence/fabric-analytics-listeners/entry-points/RendererAnalyticsListener.ts';
import { getConnectDataListener } from '@confluence/app-referentiality/entry-points/connectDataListener.ts';
import { expVal } from '@confluence/feature-experiments';
import { getMediaSSRModeAndAuthConfigMemoized } from '@confluence/fabric-media-support/entry-points/ssrMediaHelpers.ts';

import { getExtensionHandlers } from '../../lib/renderer-extensions/index.ts';

import { unsupportedContentLevelsTrackingOptions } from './fabric-feature-flags.ts';
import type { ExternalProps } from './Renderer.tsx';
import { populateExtensionProviders } from './populateExtensionProviders.ts';
import { mutateInterop } from '@confluence/apollo-relay-interop/entry-points/mutate.ts';

// ADS migration (COMMENTS-6463): tokenized border width when FG is on
const borderWidthYellowToken = token('border.width.selected');

const inlineCommentStylesCompiledTokenized = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'div[mode="stick"]': {
		zIndex: 1,
		background: token('color.background.inverse.subtle'),
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&.inline-highlight': {
			cursor: 'pointer',
			backgroundColor: token('color.background.accent.yellow.subtler'),
			borderBottom: `${borderWidthYellowToken} solid ${token('color.border.accent.yellow')}`,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'&.active-highlight': {
				backgroundColor: token('color.background.accent.yellow.subtle'),
				borderBottom: `${borderWidthYellowToken} solid ${token('color.border.accent.yellow')}`,
				boxShadow: token('elevation.shadow.overlay'),
			},

			/* Accessibility markers for screen readers: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/mark#accessibility_concerns */
			'&::before, &::after': {
				clipPath: 'inset(100%)',
				clip: 'rect(1px, 1px, 1px, 1px)',
				height: '1px',
				overflow: 'hidden',
				position: 'absolute',
				whiteSpace: 'nowrap',
				width: '1px',
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'&.first-mark-highlight::before': {
				content: 'var(--content-renderer-inline-comment-marker-start)',
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'&.last-mark-highlight::after': {
				content: 'var(--content-renderer-inline-comment-marker-end)',
			},
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&.inline-create-comment-selection': {
			backgroundColor: token('color.background.accent.yellow.subtle'),
			borderBottom: `${borderWidthYellowToken} solid ${token('color.border.accent.yellow')}`,
			boxShadow: token('elevation.shadow.overlay'),
		},
	},
});

const inlineCommentStylesCompiledLegacy = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'div[mode="stick"]': {
		zIndex: 1,
		background: token('color.background.inverse.subtle'),
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&.inline-highlight': {
			cursor: 'pointer',
			backgroundColor: token('color.background.accent.yellow.subtler'),
			borderBottom: `2px solid ${token('color.border.accent.yellow')}`,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'&.active-highlight': {
				backgroundColor: token('color.background.accent.yellow.subtle'),
				borderBottom: `2px solid ${token('color.border.accent.yellow')}`,
				boxShadow: token('elevation.shadow.overlay'),
			},

			/* Accessibility markers for screen readers: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/mark#accessibility_concerns */
			'&::before, &::after': {
				clipPath: 'inset(100%)',
				clip: 'rect(1px, 1px, 1px, 1px)',
				height: '1px',
				overflow: 'hidden',
				position: 'absolute',
				whiteSpace: 'nowrap',
				width: '1px',
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'&.first-mark-highlight::before': {
				content: 'var(--content-renderer-inline-comment-marker-start)',
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'&.last-mark-highlight::after': {
				content: 'var(--content-renderer-inline-comment-marker-end)',
			},
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&.inline-create-comment-selection': {
			backgroundColor: token('color.background.accent.yellow.subtle'),
			borderBottom: `2px solid ${token('color.border.accent.yellow')}`,
			boxShadow: token('elevation.shadow.overlay'),
		},
	},
});

// ADS migration: wrapper gated by confluence_frontend_tokenize_border (COMMENTS-6463)
const InlineCommentWrapperLegacy = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement> & { css?: unknown }
>((props, ref) => <div ref={ref} css={inlineCommentStylesCompiledLegacy} {...props} />);
InlineCommentWrapperLegacy.displayName = 'InlineCommentWrapperLegacy';

const InlineCommentWrapperTokenized = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement> & { css?: unknown }
>((props, ref) => <div ref={ref} css={inlineCommentStylesCompiledTokenized} {...props} />);
InlineCommentWrapperTokenized.displayName = 'InlineCommentWrapperTokenized';

const InlineCommentWrapper = componentWithFG(
	'confluence_frontend_tokenize_border',
	InlineCommentWrapperTokenized,
	InlineCommentWrapperLegacy,
);

const styles = cssMap({
	renderContentButton: {
		backgroundColor: token('color.background.neutral.subtle'),
		width: '100%',
	},
});

const _RERENDER_STATE_NONE = '';
const _RERENDER_STATE_REQUESTED = 'requested';

export type ComponentProps = ExternalProps;

type ProfilecardProvider = {
	cloudId: string;
	resourceClient: ProfileClient;
	getActions: ProfileCardGetActions;
};

type ComponentState = {
	_root: HTMLElement | undefined;
	rerender: string;
	taskBatcher: TaskBatcher | undefined;
	shouldRefetchContent: boolean;
};

type ExcerptSpecificProps = {
	mediaToken?: string;
};

class RendererComponent extends PureComponent<
	ComponentProps &
		WrappedComponentProps &
		RoutesContextType &
		WithNavigationPolicyProps &
		CoreInvitesContextType &
		ExternalShareContextType &
		SessionDataContextInjected & { offsetTop?: number } & ExcerptSpecificProps,
	ComponentState
> {
	state = {
		_root: undefined,
		rerender: _RERENDER_STATE_NONE,
		referentialityDataSourceProvider: new ReferentialityDataSourceProvider(),
		taskBatcher: undefined as TaskBatcher | undefined,
		shouldRefetchContent: false,
	};

	_handleActionToggle = (objectKey: ObjectKey, state: TaskState) => {
		// add the task action to be batched/updated in the batch before firing the request
		this.state.taskBatcher?.add({ objectKey, state });
	};

	_onBatchTaskUpdate = (batch: TaskAction[]) => {
		const { objectId, isPreviewMode } = this.props;

		const updateBatch = batch.map((update) => {
			const { objectKey, state } = update;
			const status = state === 'DONE' ? 'CHECKED' : 'UNCHECKED';

			return { taskId: objectKey.localId, status };
		});

		const taskBatch = {
			contentId: objectId,
			tasks: updateBatch,
			trigger: 'VIEW_PAGE',
		};

		if (!isPreviewMode) {
			mutateInterop({
				mutation: SetBatchedTaskStatusMutation,
				variables: taskBatch,
			})
				.then(() => {
					this.setState({ shouldRefetchContent: true });
				})
				.catch((error: Error): any => {
					void getAnalyticsWebClient().then((client) => {
						client.sendOperationalEvent({
							source: 'page',
							action: 'batchTaskUpdate',
							actionSubject: 'checkbox',
							attributes: {
								error: error.message || String(error),
								stack: error.stack,
							},
						});
					});
				});
		}
	};

	_handleProfileCardActions: ProfileCardGetActions = (userId) => {
		const { push } = this.props;
		const atlassianPeopleProfileUrl = getAtlassianPeopleProfileUrl(userId, null, CONTEXT_PATH);
		return [
			{
				label: this.props.intl.formatMessage({ id: 'view.profile' }),
				link: atlassianPeopleProfileUrl,
				callback: () => {
					push(getAtlassianPeopleProfileUrl(userId, null, CONTEXT_PATH));
				},
			},
		];
	};

	componentDidMount() {
		const { adf, sessionData, contentType } = this.props;

		if (adf && sessionData?.isLicensed) {
			registerGlobalMediaViewedListener({ contentType });
		}

		getConnectDataListener().init({
			emit: (localId, data) =>
				this.state.referentialityDataSourceProvider.createOrUpdate(localId, data),
		});
		const taskBatcher = new TaskBatcher(
			expVal('confluence_frontend_task_batching_interval', 'value', 3000) as number,
		);
		taskBatcher.onFlush(this._onBatchTaskUpdate);

		this.setState({ taskBatcher });
	}

	/**
	 * Needed to force an update of Atlaskit Renderer to wake-up TWP code inside that opens expand based on hash in the case where it hasn't changed.
	 * The alternative of using getDerivedStateFromProps required multiple steps which didn't work with RTL testing.
	 * Settled on normally discouraged setState inside of componentDidUpdate with an eslint-disable to get through Bamboo.
	 */
	/**
	 * When the objectId of the renderer changes, we want to reset the `shouldFetchContent` state variable so we don't
	 * cause unnecessary refetches; Calling setState behind a condition that will only happen on page transitions
	 */
	componentDidUpdate(prevProps) {
		const { objectId } = this.props;
		const { rerender } = this.state;
		if (rerender === _RERENDER_STATE_REQUESTED) {
			// eslint-disable-next-line
			this.setState({ rerender: _RERENDER_STATE_NONE });
		}

		if (objectId !== prevProps.objectId) {
			// eslint-disable-next-line
			this.setState({ shouldRefetchContent: false });
		}
	}

	componentWillUnmount() {
		const { adf, sessionData } = this.props;

		if (adf && sessionData?.isLicensed) {
			removeGlobalMediaViewedListener();
		}
	}

	getExpandHash = () => {
		if (this.state.rerender === _RERENDER_STATE_REQUESTED) {
			return '';
		} else {
			return this.props.getHash();
		}
	};

	getExtensionHandlers = (shouldIncludeMacroHandlers?: boolean): RendererExtensionHandlers => {
		const {
			adf,
			objectId,
			spaceKey,
			showTemplateVariablesInPreview,
			showTemplateInputInPreview,
			appearance,
			isPreviewMode = false,
		} = this.props;

		const { referentialityDataSourceProvider } = this.state;

		return getExtensionHandlers({
			adf,
			contentId: objectId,
			appearance: appearance as string,
			showTemplateVariablesInPreview,
			showTemplateInputInPreview,
			spaceKey,
			shouldIncludeMacroHandlers,
			shouldIncludeMacroWithReferencesHandlers: true,
			referentialityDataSourceProvider,
			fromFabricEditor: true,
			isPreviewMode,
		});
	};

	getEventHandlers = (): React.ComponentProps<typeof AkRenderer>['eventHandlers'] => {
		const { matchRoute, push } = this.props;

		return {
			...createSmartCardEventHandler({ matchRoute, push }),
		};
	};

	_setRoot = (ref) => this.setState({ _root: ref });

	/* getting type issues with ProfilecardProvider in atlaskit with ProfileCardProvider from next.
   This helps resolve the type errors */
	getProfilecardProvider = (
		cloudId: string,
		getActions: ProfileCardGetActions,
		featureFlags: FeatureFlagsType,
	): Promise<ProfilecardProvider> => {
		return getProfileCardProvider(cloudId, getActions, featureFlags);
	};

	getProviders = memoizeOne((flags) => {
		const {
			intl,
			accountId,
			contentType,
			cloudId,
			environment,
			objectId,
			spaceKey,
			isExternalShareRequest,
			sessionData,
			showTemplateInputInPreview,
			showTemplateVariablesInPreview,
			openCoreInvites,
			userRole,
			mediaToken,
			shareToken,
		} = this.props;

		const { featureFlags, activationId } = sessionData as SessionDataType;

		const isTemplateMedia = showTemplateVariablesInPreview || showTemplateInputInPreview;
		const mediaProvider = isTemplateMedia
			? createRendererTemplateMediaProvider({
					spaceKey,
					templateIds: [objectId],
				})
			: createRendererMediaProvider({
					contentId: objectId,
					spaceKey,
					isExternalShare: isExternalShareRequest,
					mediaToken,
					shareToken,
				});
		const providerFactory = ProviderFactory.create({
			mediaProvider,
			taskDecisionProvider: Promise.resolve(
				new ConfluenceTaskDecisionProvider(intl, flags, objectId, this._handleActionToggle),
			),
			cardProvider: Promise.resolve(new ConfluenceEditorCardProvider()),
		});

		if (cloudId && accountId) {
			providerFactory.setProvider(
				'mentionProvider',
				createMentionsConfig(cloudId, accountId, undefined, undefined, openCoreInvites, userRole)
					.then((config) => config.mentionResource)
					.catch((err) => {
						if (process.env.NODE_ENV !== 'production') {
							// eslint-disable-next-line no-console
							console.error('Failed to initialise mention resource ', err);
						}
						return Promise.reject();
					}),
			);
		}

		const hasAccess =
			sessionData?.accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
			sessionData?.accessStatus === AccessStatus.LICENSED_USE_ACCESS;

		if (cloudId) {
			providerFactory.setProvider(
				'emojiProvider',
				getEmojiProvider(EmojiResource, {
					cloudId,
					userId: accountId,
					allowAnonymousAccess: true,
				}),
			);
		}

		if (cloudId && hasAccess) {
			// If global view user profile is enabled then user can view profile card
			// Making promise void as in the worst case the
			void getCanViewUserProfile().then((hasViewUserProfilePermission) => {
				if (hasViewUserProfilePermission) {
					providerFactory.setProvider(
						'profilecardProvider',
						this.getProfilecardProvider(
							cloudId,
							this._handleProfileCardActions,
							featureFlags,
						) as any,
					);
				}
			});
		}

		if (isTemplateMedia) {
			const contextIdentifierProvider = async () => {
				const templateObjectId = JSON.stringify({
					templateId: objectId,
					spaceKey,
				});

				return Promise.resolve({
					containerId: spaceKey || '',
					objectId: templateObjectId,
					product: 'confluence',
				});
			};
			providerFactory.setProvider('contextIdentifierProvider', contextIdentifierProvider());
		} else if (spaceKey) {
			providerFactory.setProvider(
				'contextIdentifierProvider',
				Promise.resolve({
					containerId: spaceKey,
					objectId,
					product: 'confluence',
				}),
			);
		}

		populateExtensionProviders(providerFactory, {
			intl,
			contentId: objectId,
			containerId: spaceKey || '',
			accountId,
			cloudId,
			spaceKey,
			contentType,
			environment,
			featureFlags,
			extensionHandlers: this.getExtensionHandlers(true),
			showTemplateInputInPreview,
			showTemplateVariablesInPreview,
			activationId,
		});

		return providerFactory;
	});

	decodeHash = (hash) => {
		let decodedHash = '';
		try {
			// try to decode the hash
			decodedHash = decodeURIComponent(hash);
		} catch {
			// if hash is not encoded, it will throw a URI Malformed error,
			// which we will then return the original hash
			decodedHash = hash;
		}
		return decodedHash;
	};

	handleClicksInRenderer = (event) => {
		/**
		 * Covering a corner case:
		 * If the user
		 * - has followed a deep link to a header inside an expand
		 * - then closes that expand
		 * - then clicks a TOC link to the same header
		 * the renderer code will not see a change in the deep link has, so it won't run which leaves the user scrolled
		 * to a closed expand element.
		 * This code detects the above situation and sets the rerender state property to begin a double re-render of the
		 * renderer (once with no hash and once with the original hash). This lets the renderer code see a change so it
		 * opens the expand.
		 */
		const deepLinkHash = event.target.hash;
		if (deepLinkHash === `#${this.getExpandHash()}`) {
			const tocTarget = document.getElementById(this.decodeHash(this.getExpandHash()));
			if (tocTarget) {
				const expandParent = tocTarget.closest("[data-node-type='expand']");
				// If the TOC link points to a header inside a closed expand
				if (expandParent && expandParent.getAttribute('data-expanded') === 'false') {
					this.setState({
						rerender: _RERENDER_STATE_REQUESTED,
					});
				}
			}
		}
	};

	renderRenderer = (extensionHandlers, dataProviders) => {
		const {
			adf,
			id,
			className,
			style,
			allowInlineComments,
			ignoreHeadingAnchorLinksFeatureFlag,
			renderWhenReady,
			onComplete,
			objectId,
			sessionData,
			allowPlaceholderText = false,
			annotationProvider,
			allowAnnotations = false,
			UNSTABLE_allowTableAlignment = true,
			UNSTABLE_allowTableResizing = true,
			spaceKey,
			isExternalShareRequest,
			mediaToken,
		} = this.props;

		const { shouldRefetchContent } = this.state;

		if (!adf) {
			return null;
		}

		// Allow disabling the feature for previews.
		const allowHeadingAnchorLinks = !ignoreHeadingAnchorLinksFeatureFlag;

		const { offsetTop } = this.props;
		const stickyHeaders = offsetTop !== undefined && {
			offsetTop,
		};

		const { featureFlags, isAdminHubAIEnabled } = sessionData as SessionDataType;

		// used for renderer's annotation provider
		const localRef = React.createRef() as React.RefObject<HTMLDivElement>;

		const shouldSSRsmartLinks = isPageSSRd() && !isEmbeddedConfluence_DO_NOT_USE();

		const mediaSSR = getMediaSSRModeAndAuthConfigMemoized({
			contentId: objectId,
			spaceKey,
			isExternalShare: isExternalShareRequest,
			mediaToken,
		});

		const content = (
			<RendererAnalyticsListener objectId={objectId}>
				{/**
				 * If you modify any of the AkRenderer props below, please also
				 * update `next/packages/adf-renderer/src/ADFRenderer.tsx`
				 * to maintain parity with the next version of the renderer.
				 */}
				<WithAnnotationsWrapper
					annotationProvider={annotationProvider}
					rendererRef={localRef}
					adfDocument={(this.props.navigationPolicy?.shimAdfUrls(adf) ?? adf) as DocNode}
					updateDocument={() => {}} /* unnecessary right now */
					isNestedRender
				>
					<AkRenderer
						appearance={this.props.appearance}
						contentMode={this.props.contentMode}
						document={(this.props.navigationPolicy?.shimAdfUrls(adf) ?? adf) as DocNode}
						adfStage="stage0"
						dataProviders={dataProviders}
						eventHandlers={this.getEventHandlers()}
						extensionHandlers={extensionHandlers}
						featureFlags={{}}
						portal={this.state._root}
						onComplete={onComplete}
						allowColumnSorting
						allowHeadingAnchorLinks={{
							allowNestedHeaderLinks: allowHeadingAnchorLinks,
							activeHeadingId: this.decodeHash(this.getExpandHash()),
						}}
						allowAltTextOnImages
						media={{
							allowLinking: true,
							allowCaptions: true,
							featureFlags: getMediaFeatureFlags(featureFlags),
							...(expVal('confluence_frontend_fix_macro_media_flickering', 'isEnabled', false)
								? { ssr: mediaSSR }
								: {}),
						}}
						stickyHeaders={stickyHeaders}
						useSpecBasedValidator
						allowCopyToClipboard
						allowWrapCodeBlock
						analyticsEventSeverityTracking={ANALYTICS_EVENT_SEVERITY_TRACKING}
						allowSelectAllTrap
						unsupportedContentLevelsTracking={unsupportedContentLevelsTrackingOptions()}
						allowPlaceholderText={allowPlaceholderText}
						allowCustomPanels
						smartLinks={{ hideHoverPreview: false, ssr: shouldSSRsmartLinks }}
						allowAnnotations={allowAnnotations}
						annotationProvider={annotationProvider}
						UNSTABLE_allowTableAlignment={UNSTABLE_allowTableAlignment}
						UNSTABLE_allowTableResizing={UNSTABLE_allowTableResizing}
					/>
				</WithAnnotationsWrapper>
			</RendererAnalyticsListener>
		);

		const rendererContent = fg('custom-interactive-elements-not-keyboard-focusable') ? (
			<Pressable xcss={styles.renderContentButton} onClick={this.handleClicksInRenderer}>
				{content}
			</Pressable>
		) : (
			// eslint-disable-next-line
			<div onClick={this.handleClicksInRenderer}>{content}</div>
		);

		return (
			<InlineCommentWrapper
				id={id}
				data-inline-comments-target={allowInlineComments ? 'true' : 'false'}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
				className={className}
				ref={this._setRoot}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				style={
					{
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						...style,
						'--content-renderer-inline-comment-marker-start': this.props.intl.formatMessage(
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
							messages.contentRendererInlineCommentMarkerStart,
						),
						'--content-renderer-inline-comment-marker-end': this.props.intl.formatMessage(
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
							messages.contentRendererInlineCommentMarkerEnd,
						),
					} as CSSProperties
				}
				data-test-appearance={this.props.appearance}
			>
				<ConfluenceSmartCardProvider
					{...getSmartCardRenderers()}
					isAdminHubAIEnabled={isAdminHubAIEnabled}
				>
					{rendererContent}
				</ConfluenceSmartCardProvider>
				{renderWhenReady && renderWhenReady()}
				<ContentRefetchHandler contentId={objectId} shouldRefetch={shouldRefetchContent} />
			</InlineCommentWrapper>
		);
	};

	render() {
		window.performance.mark('CFP-63.view.page.classic.renderer.rendered');

		return (
			<Subscribe to={[FlagsStateContainer]}>
				{(flags: FlagsStateContainer) => {
					const dataProviders = this.getProviders(flags);

					return this.renderRenderer(this.getExtensionHandlers(false), dataProviders);
				}}
			</Subscribe>
		);
	}
}

export const RendererComponentWithIntl = injectIntl(withNavigationPolicy(RendererComponent));

class RendererComponentWithBanner extends PureComponent<
	ComponentProps &
		RoutesContextType &
		CoreInvitesContextType &
		ExternalShareContextType &
		SessionDataContextInjected,
	ComponentState
> {
	render() {
		const { allowStickyHeaders } = this.props;

		if (allowStickyHeaders === false) {
			return <RendererComponentWithIntl {...this.props} />;
		}

		return (
			<Subscribe to={[BannerStateContainer, FloatingBannerStateContainer]}>
				{(bannerState: BannerStateContainer, floatingBanners: FloatingBannerStateContainer) => {
					const offsetTop = bannerState.getTotalHeight() + floatingBanners.getFloatingHeight();
					return <RendererComponentWithIntl offsetTop={offsetTop} {...this.props} />;
				}}
			</Subscribe>
		);
	}
}

export default withErrorBoundary({
	attribution: Attribution.PAGE_EXTENSIONS,
})(
	withCoreInvitesContext(
		withExternalShareContext(withSessionData(withRoutesContext(RendererComponentWithBanner))),
	),
);
